import { createAction, props } from '@ngrx/store';
import { AuthConnection } from './auth-connection.model';
import { UserAuthKeyExpiration } from './auth-key-expired.model';
import { AuthToken, HorizonRedirect, LitmosSso, ZendeskSso } from 'src/app/modules/core/store/models/auth-token.model';
import { LoginModel } from './login.model';
import { CultureCodeWithLabel, Language } from 'src/app/modules/core/languages/language.model';
import { UserContext } from './user-context.model';

//
// Connection actions
//
export const getConnection = createAction('[Connection/API] Get Connections', props<{ loginData: Partial<LoginModel> }>());
export const getConnectionSuccess = createAction(
  '[Connection/API] Get Connections Success',
  props<{ connection: AuthConnection; username: string; cultureCode: string }>()
);
export const getConnectionFailed = createAction('[Connection/API] Get Connections Failed', props<{ err: any }>());
export const resetConnections = createAction('[Connection/UI] Reset Connections');
export const startAuth0Connection = createAction('[Connection/API] Start Auth0 Connection', props<{ connection: AuthConnection; cultureCode: string }>());

//
// Login page actions
//
export const loginStepEmail = createAction('[Login/Page] Login Step Email', props<{ loginData: Partial<LoginModel> }>());
export const loginStepEmailSuccess = createAction('[Login/Page] Login Step Email Success', props<{ token: AuthToken }>());
export const loginStepEmailFailed = createAction('[Login/Page] Login Step Email Failed', props<{ err: any }>());
export const showPassword = createAction('[Login/Page] Show Password', props<{ connection: AuthConnection; username: string }>());
export const setCompletingLogin = createAction('[Login/Page] Completing Login', props<{ isCompleting: boolean }>());
export const setError = createAction('[Login/Page] Set Error', props<{ err: { message: string } }>());
export const setIsWorking = createAction('[Login/Page] Is Working', props<{ isWorking: boolean }>());

export const processAuth0LoginResult = createAction('[Login/API] Process Auth0 Login', props<{ code: string; state: string; }>());
export const processAuth0LoginResultSuccess = createAction('[Login/API] Process Auth0 Login Success');
export const processHorizonLoginResult = createAction('[Login/API] Process Horizon Login', props<{ jwt: string; org: string; }>());
export const processHorizonLoginResultSuccess = createAction('[Login/API] Process Horizon Login Success');
export const processHorizonRedirect = createAction('[Login/API] Process Horizon Redirect');
export const processHorizonRedirectSuccess = createAction('[Login/API] Process Horizon Redirect Success', props<{ path: HorizonRedirect}>());

export const loginUserNamePassword = createAction(
  '[Login/API] Login Username Password',
  props<{ loginData: { username: string; password: string; cultureCode: string } }>()
);
export const loginUserNamePasswordSuccess = createAction('[Login/API] Login Username Password Success');

export const backToEmail = createAction('[Login/Page] Back To Email');

export const resetLogin = createAction('[Login/Page] Reset Login');

export const logout = createAction('[Login/API] Logout');

export const logoutSuccess = createAction('[Login/API] Logout Success');

export const refreshAccessToken = createAction('[Login/API] Refresh Access Token');
export const setToken = createAction('[Auth] Set Token', props<{ token: AuthToken }>());

export const setExpired = createAction('[Login/Page] Get Auth Key Expiration Status', props<{ expirationStatus: UserAuthKeyExpiration }>());

export const isResetCodeExpired = createAction('[Login/Page] Get Reset Code Expiration Status', props<{ username: string; authKey: string; }>());
export const resendInviteLink = createAction('[Login/Page] Send Invite Link', props<{ username: string; }>());
export const setNewUserPassword = createAction('[Login/Page] Set New User Password With Auth Code', props<{ username: string; newPassword: string; authKey: string; }>());

export const sendPasswordResetLink = createAction('[Login/Page] Send Forgot Password Link', props<{ username: string; }>());
export const setUserPassword = createAction('[Login/Page] Reset User Password With Auth Code', props<{ username: string; newPassword: string; authKey: string; }>());
export const isInviteCodeExpired = createAction('[Login/Page] Get Invite Code Expiration Status', props<{ username: string; authKey: string; }>());

export const changeOrganization = createAction('[Login/API] Change Organization', props<{ organizationUId: string }>());
export const changingOrganization = createAction('[Login/API] Changing Organization With New Token', props<{ token: AuthToken }>());
export const loadAuthTokenByOrganization = createAction('[Login/API] Load Auth Token By Organization', props<{ token: AuthToken }>());
export const clearAllStates = createAction('[Login/API] Clear All States');

export const routeToSharedMap = createAction('[Login/API] Route To Shared Map', props<{ sharedCode: string; target: string }>());
export const sharedMapAuthWorking = createAction('[Login/API] Set Shared Map Authorization Working', props<{ isWorking: boolean }>());
export const setMapLinkError = createAction('[Login/Page] Set Error For A Map Link', props<{ err: { message: string } }>());

export const reloadBrowser = createAction('[Auth] Reload Browser');
export const routeAfterLoginSuccess = createAction('[Login/API] Route after Login Success', props<{ userContext: UserContext; checkPolicy: boolean; }>());

export const routeToSharedDashboard = createAction('[Login/API] Route To Shared Dashboard', props<{ sharedCode: string }>());
export const sharedDashboardAuthWorking = createAction('[Login/API] Set Shared Dashboard Authorization Working', props<{ isWorking: boolean }>());
export const setDashboardLinkError = createAction('[Login/Page] Set Error For A Dashboard Link', props<{ err: { message: string } }>());

export const routeToExternalLink = createAction('[Login/API] Route To External Link', props<{ sharedCode: string }>());
export const externalLinkAuthWorking = createAction('[Login/API] Set External Link Authorization Working', props<{ isWorking: boolean }>());
export const setExternalLinkError = createAction('[Login/Page] Set Error For External Link', props<{ err: { message: string } }>());

export const loadAvailableLanguagesForLogin = createAction('[Login/API] Load Available Languages For Login');
export const loadAvailableLanguagesComplete = createAction(
  '[Login/API] Load Available Languages Complete',
  props<{ languages: string[] }>()
);

export const permissionsChanged = createAction('[Login/API] Permissions Changed');

export const showAcceptancePage = createAction('[Login/Page] Show Acceptance Page');
export const hideAcceptancePage = createAction('[Login/Page] Hide Acceptance Page');

export const showBusinessUnitsWarning = createAction('[Login/Page] Show Business Units Warning');
export const hideBusinessUnitsWarning = createAction('[Login/Page] Hide Business Units Warning');
export const setBusinessUnitsWarningAccepted = createAction('[Login/Page] Business Units Warning Accepted');

export const getZendeskSso = createAction('[Login/API] Get Zendesk SSO');
export const getZendeskSsoSuccess = createAction('[Login/API] Redirect to Zendesk', props<{ ssoToken: ZendeskSso }>());

export const getLitmosSso = createAction('[Login/API] Get Litmos SSO');
export const getLitmosSsoSuccess = createAction('[Login/API] Redirect to Litmos', props<{ getLoginKeyResponse: LitmosSso }>());
